import { Close } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import {
    Avatar,
    Button,
    Checkbox,
    CircularProgress,
    FormControl,
    FormControlLabel,
    Grid,
    InputAdornment,
    InputLabel,
    Menu,
    MenuItem,
    OutlinedInput,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import React from 'react';
import SelectVehicleModal from '../../modal/SelectVehicleModal';

// ── Constants ──────────────────────────────────────────────────────────────
const ACCEPTED_STATUS_OPTIONS = [
    { _id: 'waiting_for_drop_off', label: 'Waiting for Drop off' },
    { _id: 'vehicle_dropped_off', label: 'Vehicle Dropped Off' },
    { _id: 'in_progress', label: 'In Progress' },
    { _id: 'ready_for_pick_up', label: 'Ready for pickup' },
    { _id: 'customer_paid', label: 'Customer Paid' },
    { _id: 'mark_as_no_show', label: 'Marked as No Show' },

    { _id: 'cancelled', label: 'Cancelled' },
];

// ── Styles ─────────────────────────────────────────────────────────────────
const styles = {
    menuPadding: { padding: '0', width: 'calc(100% + 10px)!important' },
    formControlLabel: { padding: '6px 16px', marginRight: '0!important', width: '100%' },
    filterBtn: (isActive) => ({
        color: isActive ? 'white!important' : 'black!important',
        fontWeight: 500,
        lineHeight: '24px',
        padding: '6px 12px!important',
        height: '40px',
    }),
    countAvatar: {
        bgcolor: 'white',
        color: 'primary.main',
        fontSize: '14px!important',
        fontWeight: '500!important',
        width: 24,
        height: 24,
    },
    clearBtn: {
        background: '#0000001A',
        color: '#000000',
        boxShadow: '0px 1px 4px 0px rgba(0,0,0,0.25)',
        '&:hover': { background: '#0000001A', boxShadow: '0px 1px 4px 0px rgba(0,0,0,0.25)' },
        fontWeight: 500,
        lineHeight: '24px',
    },
    datePicker: { width: 240, minWidth: 240, maxWidth: 240 },
    datePickerTextField: { sx: { width: 240, minWidth: 240, maxWidth: 240 }, inputProps: { id: 'appointment-date-input' } },
    loaderContainer: { py: 2 },
};

// ── MultiSelectFilter sub-component ───────────────────────────────────────
const MultiSelectFilter = ({ label, options, selected = [], onChange, anchorEl, onOpen, onClose, loading }) => {
    const open = Boolean(anchorEl);
    const isActive = selected.length > 0;

    return (
        <>
            <Button
                variant={isActive ? 'contained' : 'text'}
                color="primary"
                onClick={onOpen}
                sx={styles.filterBtn(isActive)}
                startIcon={isActive && <Avatar sx={styles.countAvatar}>{selected.length}</Avatar>}
                endIcon={<ExpandMoreIcon />}
            >
                {label}
            </Button>

            <Menu anchorEl={anchorEl} open={open} onClose={onClose} PaperProps={{ style: { maxHeight: 400 } }}>
                {loading ? (
                    <MenuItem>
                        <Grid container justifyContent="center" alignItems="center" sx={styles.loaderContainer}>
                            <CircularProgress size={24} />
                        </Grid>
                    </MenuItem>
                ) : options.length === 0 ? (
                    <MenuItem disabled>No services found</MenuItem>
                ) : (
                    options.map((opt) => (
                        <MenuItem key={opt._id} sx={styles.menuPadding}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={selected.includes(opt._id)}
                                        onChange={() => {
                                            const updated = selected.includes(opt._id) ? selected.filter((s) => s !== opt._id) : [...selected, opt._id];
                                            onChange(updated);
                                        }}
                                    />
                                }
                                label={opt.label}
                                sx={styles.formControlLabel}
                            />
                        </MenuItem>
                    ))
                )}
            </Menu>
        </>
    );
};

// ── Main component ─────────────────────────────────────────────────────────
const AppointmentRequestFilters = ({
    tabValue,
    selectDate,
    onDateChange,
    selectedStatuses,
    onStatusChange,
    vehicleOpen,
    onVehicleOpen,
    onVehicleCancel,
    onVehicleApply,
    yearData,
    makeData,
    modelData,
    licenseNumber,
    fleetUnitNumber,
    provinceId,
    vinFilter,
    search,
    onSearchChange,
    hasClearableFilters,
    clearFilterLabel,
    onClearFilters,
}) => {
    const [statusAnchorEl, setStatusAnchorEl] = React.useState(null);

    const vehicleFieldCount = [licenseNumber?.trim?.(), fleetUnitNumber?.trim?.(), vinFilter?.trim?.(), yearData, makeData, modelData, provinceId].filter(
        Boolean
    ).length;

    const statusOptions = tabValue === 1 ? ACCEPTED_STATUS_OPTIONS : [];
    const vehicleIsActive = vehicleFieldCount > 0;

    return (
        <Grid container marginBottom={'16px'} alignItems={'center'} justifyContent={'space-between'}>
            {/* ── Left: filter controls ── */}
            <Grid container item xs={12} sm={8} md={8} justifyContent={'flex-start'} alignItems={'center'}>
                {/* Date Picker — all tabs */}
                <Grid item xs="auto" padding={'6px'}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label="Select date"
                            value={selectDate}
                            onChange={onDateChange}
                            sx={styles.datePicker}
                            slotProps={{
                                textField: styles.datePickerTextField,
                                field: { clearable: true },
                            }}
                        />
                    </LocalizationProvider>
                </Grid>

                {/* Filter by Vehicle — all tabs */}
                <Grid item xs="auto" padding={'6px'}>
                    <Button
                        variant={vehicleIsActive ? 'contained' : 'text'}
                        color="primary"
                        onClick={onVehicleOpen}
                        sx={styles.filterBtn(vehicleIsActive)}
                        startIcon={vehicleIsActive && <Avatar sx={styles.countAvatar}>{vehicleFieldCount}</Avatar>}
                        endIcon={<ExpandMoreIcon />}
                    >
                        FILTER BY VEHICLE
                    </Button>
                    <SelectVehicleModal
                        open={vehicleOpen}
                        onCancel={onVehicleCancel}
                        onApply={onVehicleApply}
                        yearData={yearData}
                        makeData={makeData}
                        modelData={modelData}
                        licenseNumber={licenseNumber}
                        fleetUnitNumber={fleetUnitNumber}
                        provinceId={provinceId}
                        vinFilter={vinFilter}
                    />
                </Grid>

                {/* Status filter — Accepted tab (1) only */}
                {tabValue === 1 && (
                    <Grid item xs="auto" padding={'6px'}>
                        <MultiSelectFilter
                            label="STATUS"
                            options={statusOptions}
                            selected={selectedStatuses}
                            onChange={onStatusChange}
                            anchorEl={statusAnchorEl}
                            onOpen={(e) => setStatusAnchorEl(e.currentTarget)}
                            onClose={() => setStatusAnchorEl(null)}
                        />
                    </Grid>
                )}

                {/* Clear Filters */}
                {hasClearableFilters && (
                    <Grid item xs="auto" padding={'6px'}>
                        <Button variant="contained" startIcon={<Close />} onClick={onClearFilters} sx={styles.clearBtn}>
                            {clearFilterLabel}
                        </Button>
                    </Grid>
                )}
            </Grid>

            {/* ── Right: Search ── */}
            <Grid container item xs={12} sm={4} md={4} justifyContent={'flex-end'} padding={'6px'}>
                <FormControl fullWidth>
                    <InputLabel htmlFor="appointment-search">Search</InputLabel>
                    <OutlinedInput
                        id="appointment-search"
                        placeholder="Customer name, phone, email etc..."
                        startAdornment={
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        }
                        value={search}
                        label="Search"
                        onChange={(e) => onSearchChange(e.target.value)}
                    />
                </FormControl>
            </Grid>
        </Grid>
    );
};

export default AppointmentRequestFilters;
